import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { HiKey, HiUsers, HiOfficeBuilding, HiDocumentText } from 'react-icons/hi';
import { Card } from '../components/ui';
import { useAuthStore } from '../store/authStore';
import { listLicenses } from '../services/licenses.api';
import { listCustomers } from '../services/customers.api';
import { listPartners } from '../services/partners.api';
import { listRequests } from '../services/requests.api';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  href?: string;
}

function StatCard({ title, value, icon: Icon, color, href }: StatCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (href) {
      navigate(href);
    }
  };

  return (
    <Card
      className={href ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}
      onClick={handleClick}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OWNER';
  const isInvoice = user?.role === 'INVOICE_TEAM';
  const isPartner = user?.role === 'PARTNER';

  const { data: licenses } = useQuery({
    queryKey: ['licenses', 'stats'],
    queryFn: () => listLicenses({ limit: 1 }),
    enabled: isAdmin || isInvoice,
  });

  const { data: customers } = useQuery({
    queryKey: ['customers', 'stats'],
    queryFn: () => listCustomers({ limit: 1 }),
    enabled: isAdmin || isInvoice || isPartner,
  });

  const { data: partners } = useQuery({
    queryKey: ['partners', 'stats'],
    queryFn: () => listPartners({ limit: 1 }),
    enabled: isAdmin || isInvoice,
  });

  const { data: requests } = useQuery({
    queryKey: ['requests', 'stats'],
    queryFn: () => listRequests({ limit: 1, status: 'PENDING' }),
    enabled: isAdmin || isInvoice,
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome back, {user?.firstName}!
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {(isAdmin || isInvoice) && (
          <StatCard
            title="Total Licenses"
            value={licenses?.total ?? 0}
            icon={HiKey}
            color="bg-primary-600"
            href="/licenses"
          />
        )}

        {(isAdmin || isInvoice || isPartner) && (
          <StatCard
            title="Customers"
            value={customers?.total ?? 0}
            icon={HiUsers}
            color="bg-green-600"
            href="/customers"
          />
        )}

        {(isAdmin || isInvoice) && (
          <StatCard
            title="Partners"
            value={partners?.total ?? 0}
            icon={HiOfficeBuilding}
            color="bg-purple-600"
            href="/partners"
          />
        )}

        {(isAdmin || isInvoice) && (
          <StatCard
            title="Pending Requests"
            value={requests?.total ?? 0}
            icon={HiDocumentText}
            color="bg-orange-600"
            href="/requests"
          />
        )}
      </div>

      {user?.role === 'CUSTOMER' && (
        <Card className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Licenses</h2>
          <p className="text-gray-500">
            View and manage your licenses from the Licenses page.
          </p>
        </Card>
      )}

      {isPartner && (
        <Card className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Partner Portal</h2>
          <p className="text-gray-500">
            Manage your customers and submit license requests from the sidebar menu.
          </p>
        </Card>
      )}
    </div>
  );
}
